import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Pencil, Power, PowerOff, Search, X } from 'lucide-react';
import '../../responsive.css';
import Sidebar from '../../components/Sidebar';
import { onAdminRefresh } from '../../utils/adminSocket';
import Navbar from '../../components/Navbar';
import ConfirmDialog from '../../components/ConfirmDialog';
import Pagination, { paginate } from '../../components/Pagination';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import {
    IconStore, IconClock, IconCheck, IconX, IconTrash, IconBan, IconUsers,
} from '../../components/AdminIcons';

const C = {
    bg: 'var(--admin-bg)',
    panel: 'var(--admin-surface)',
    border: 'var(--admin-border)',
    text: 'var(--admin-text)',
    dim: 'var(--admin-text-secondary)',
    gold: 'var(--admin-primary)',
    green: '#059669',
    amber: '#B45309',
    red: '#DC2626',
    violet: '#7C3AED',
};

const DATE_RANGES = [
    { id: 'all', label: 'All time' },
    { id: 'today', label: 'Today' },
    { id: 'yesterday', label: 'Yesterday' },
    { id: 'week', label: 'Last 7 days' },
    { id: 'month', label: 'Last 30 days' },
];

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };

const inRange = (dateValue, range) => {
    if (range === 'all') return true;
    if (!dateValue) return false;
    const at = new Date(dateValue);
    const today = startOfDay(new Date());
    const dayMs = 24 * 60 * 60 * 1000;
    if (range === 'today') return at >= today;
    if (range === 'yesterday') return at >= new Date(today - dayMs) && at < today;
    if (range === 'week') return at >= new Date(today - 6 * dayMs);
    if (range === 'month') return at >= new Date(today - 29 * dayMs);
    return true;
};

const matchesQuery = (query, fields) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    const digits = q.replace(/\D/g, '');
    return fields.some(value => {
        const text = String(value || '').toLowerCase();
        // Phone numbers: match on digits so "98765 43210" and "9876543210" both work.
        return text.includes(q) || (digits.length >= 3 && text.replace(/\D/g, '').includes(digits));
    });
};

const Services = () => {
    const navigate = useNavigate();
    const [vendors, setVendors] = useState([]);
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('pending');
    const [pendingDelete, setPendingDelete] = useState(null);
    const [pendingToggle, setPendingToggle] = useState(null);
    const [page, setPage] = useState(1);
    const [query, setQuery] = useState('');
    const [range, setRange] = useState('all');

    useEffect(() => {
        fetchVendors();
        fetchServices();
        // realtime: a provider who just registered shows up under Pending without a reload
        return onAdminRefresh(() => { fetchVendors(); fetchServices(); });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchVendors = async () => {
        try {
            const res = await API.get('/admin/vendors');
            setVendors(res.data.vendors || []);
        } catch (err) {
            toast.error('Failed to load vendors');
        } finally {
            setLoading(false);
        }
    };

    const fetchServices = async () => {
        try {
            const res = await API.get('/services');
            setServices(res.data.services || []);
        } catch (err) {
            console.log('No services');
        }
    };

    const approveVendor = async (id) => {
        try {
            await API.put(`/admin/vendors/${id}/approve`);
            toast.success('Vendor approved');
            fetchVendors();
        } catch (err) {
            toast.error('Failed to approve');
        }
    };

    const rejectVendor = async (id) => {
        try {
            await API.put(`/admin/vendors/${id}/reject`);
            toast.success('Vendor rejected');
            fetchVendors();
        } catch (err) {
            toast.error('Failed to reject');
        }
    };

    // Account access: a deactivated provider cannot log in (enforced server-side on every login path).
    const confirmToggleAccount = async () => {
        const vendor = pendingToggle;
        setPendingToggle(null);
        if (!vendor) return;
        try {
            const res = await API.put(`/admin/vendors/${vendor._id}/toggle-active`);
            toast.success(res.data.message);
            setVendors(current => current.map(v => (v._id === vendor._id ? { ...v, isActive: res.data.vendor.isActive } : v)));
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to update account access');
        }
    };

    const changeTab = (id) => { setActiveTab(id); setPage(1); };

    const deleteVendor = (id) => setPendingDelete({ type: 'vendor', id });

    const deleteService = (id, name) => setPendingDelete({ type: 'service', id, name });

    const confirmDelete = async () => {
        const target = pendingDelete;
        if (!target) return;
        setPendingDelete(null);
        if (target.type === 'vendor') {
            try {
                await API.delete(`/admin/vendors/${target.id}`);
                toast.success('Vendor deleted');
                fetchVendors();
            } catch (err) {
                toast.error('Failed to delete');
            }
        } else {
            try {
                await API.delete(`/admin/services/${target.id}`);
                toast.success('Service deleted');
                fetchServices();
            } catch (err) {
                toast.error('Failed to delete service');
            }
        }
    };

    const verifyService = async (id, isVerified) => {
        try {
            await API.put(`/admin/services/${id}/verify`, { isVerified: !isVerified });
            toast.success(isVerified ? 'Service unverified' : 'Service verified');
            fetchServices();
        } catch (err) {
            toast.error('Failed to update service');
        }
    };

    const parentVendorFor = service => {
        const vendorId = service.vendor?._id || service.vendor;
        const matchedAccount = vendorId
            ? vendors.find(vendor => String(vendor._id) === String(vendorId))
            : vendors.find(vendor => service.mobile && vendor.mobile === service.mobile);
        return matchedAccount || (service.vendor && typeof service.vendor === 'object' ? service.vendor : null);
    };

    const vendorMatches = (v) => inRange(v.createdAt, range)
        && matchesQuery(query, [v.businessName, v.ownerName, v.email, v.mobile, v.category, v.city, v.district]);
    const serviceMatches = (s) => inRange(s.createdAt, range)
        && matchesQuery(query, [s.businessName, s.ownerName, s.email, s.mobile, s.phone, s.contactNumber, s.category, s.city, s.district, parentVendorFor(s)?.businessName, parentVendorFor(s)?.ownerName, parentVendorFor(s)?.providerId]);

    const pending = vendors.filter(v => !v.isApproved && vendorMatches(v));
    const approved = vendors.filter(v => v.isApproved && vendorMatches(v));
    const shownServices = services.filter(serviceMatches);
    // after a delete, approve or filter the current page may no longer exist: go back to the last page instead of showing an empty list
    const shownCount = activeTab === 'pending' ? pending.length : activeTab === 'approved' ? approved.length : shownServices.length;
    useEffect(() => {
        const lastPage = Math.max(1, Math.ceil(shownCount / 10));
        if (page > lastPage) setPage(lastPage);
    }, [shownCount, page]);
    const filtersActive = query.trim() !== '' || range !== 'all';
    const clearFilters = () => { setQuery(''); setRange('all'); setPage(1); };

    const stats = [
        { label: 'Total Vendors', value: vendors.length, accent: C.gold, Icon: IconUsers },
        { label: 'Pending Approval', value: vendors.filter(v => !v.isApproved).length, accent: C.amber, Icon: IconClock },
        { label: 'Approved', value: vendors.filter(v => v.isApproved).length, accent: C.green, Icon: IconCheck },
        { label: 'Total Services', value: services.length, accent: C.violet, Icon: IconStore },
    ];

    const tabs = [
        { id: 'pending', label: `Pending (${pending.length})` },
        { id: 'approved', label: `Approved (${approved.length})` },
        { id: 'services', label: `Services (${shownServices.length})` },
    ];

    const VendorCard = ({ v, isPending }) => {
        const active = v.isActive !== false;
        return (
        <div className="resp-vendor-card" style={{ ...styles.vendorCard, ...(active ? {} : styles.vendorCardInactive) }}>
            <div style={styles.vendorInfo}>
                <div style={styles.vendorAvatar}>
                    {(v.businessName || 'V').charAt(0).toUpperCase()}
                </div>
                <div>
                    <div style={styles.vendorName}>{v.businessName}
                        {!active && <span style={{ ...styles.chip, marginLeft: '10px', borderColor: C.red, color: C.red }}><PowerOff size={11} /> Account inactive</span>}
                    </div>
                    <div style={styles.vendorMeta}>{v.ownerName} &middot; {v.mobile}</div>
                    <div style={styles.vendorMeta}>{v.email}</div>
                    <div style={styles.vendorMeta}>{v.category} &middot; {v.city}{v.district ? `, ${v.district}` : ''}</div>
                    <div style={styles.vendorMeta}>Registered {new Date(v.createdAt).toLocaleDateString('en-IN')}</div>
                </div>
            </div>
            <div className="resp-card-actions" style={styles.vendorActions}>
                <button style={{ ...styles.btn, borderColor: C.violet, color: C.violet }}
                    onClick={() => navigate(`/service-providers/${v._id}`)}>
                    <Eye size={13} /> View
                </button>
                <button style={{ ...styles.btn, borderColor: C.gold, color: C.gold }}
                    onClick={() => navigate(`/service-providers/${v._id}/edit`)}>
                    <Pencil size={13} /> Edit
                </button>
                {isPending ? (
                    <>
                        <span style={{ ...styles.chip, borderColor: C.amber, color: C.amber }}>
                            <IconClock size={12} color={C.amber} /> Pending
                        </span>
                        <button style={{ ...styles.btn, borderColor: C.green, color: C.green }}
                            onClick={() => approveVendor(v._id)}>
                            <IconCheck size={13} color={C.green} /> Approve
                        </button>
                        <button style={{ ...styles.btn, borderColor: C.red, color: C.red }}
                            onClick={() => rejectVendor(v._id)}>
                            <IconX size={13} color={C.red} /> Reject
                        </button>
                    </>
                ) : (
                    <>
                        <span style={{ ...styles.chip, borderColor: C.green, color: C.green }}>
                            <IconCheck size={12} color={C.green} /> Approved
                        </span>
                        <button style={{ ...styles.btn, borderColor: active ? C.amber : C.green, color: active ? C.amber : C.green }}
                            onClick={() => setPendingToggle(v)}
                            title={active ? 'Deactivate account — they will not be able to log in' : 'Activate account'}>
                            {active ? <><PowerOff size={13} /> Deactivate</> : <><Power size={13} /> Activate</>}
                        </button>
                        <button style={{ ...styles.btn, borderColor: C.dim, color: C.dim }}
                            onClick={() => rejectVendor(v._id)} title="Remove approval — the provider goes back to Pending">
                            <IconBan size={13} color={C.dim} /> Unapprove
                        </button>
                        <button style={{ ...styles.btn, borderColor: C.red, color: C.red }}
                            onClick={() => deleteVendor(v._id)}>
                            <IconTrash size={13} color={C.red} /> Delete
                        </button>
                    </>
                )}
            </div>
        </div>
        );
    };

    return (
        <div style={{ display: 'flex', minHeight: '100vh', background: C.bg }}>
            <Sidebar />
            <div className="admin-content" style={{ flex: 1, minWidth: 0 }}>
                <Navbar title="Manage Service Providers" />
                <div className="resp-inner" style={styles.inner}>

                    {/* Stats */}
                    <div className="resp-grid-4" style={styles.statsRow}>
                        {stats.map(s => (
                            <div key={s.label} style={styles.statCard}>
                                <div style={{ ...styles.statAccent, background: s.accent, boxShadow: `0 0 14px ${s.accent}66` }} />
                                <div style={styles.statTop}>
                                    <div style={styles.statValue}>{s.value}</div>
                                    <div style={{ ...styles.statIcon, borderColor: `${s.accent}55` }}>
                                        <s.Icon size={17} color={s.accent} />
                                    </div>
                                </div>
                                <div style={styles.statLabel}>{s.label}</div>
                            </div>
                        ))}
                    </div>

                    {/* Tabs */}
                    <div className="resp-tabs" style={styles.tabs}>
                        {tabs.map(tab => (
                            <button key={tab.id}
                                style={{ ...styles.tabBtn, ...(activeTab === tab.id ? styles.tabBtnActive : {}) }}
                                onClick={() => changeTab(tab.id)}>
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Search + registration date filter */}
                    <div style={styles.filterBar}>
                        <label style={styles.searchBox}>
                            <Search size={15} color="var(--admin-text-muted)" />
                            <input
                                style={styles.searchInput}
                                placeholder="Search name, owner, email or phone number…"
                                value={query}
                                onChange={e => { setQuery(e.target.value); setPage(1); }}
                            />
                            {query && <button type="button" style={styles.clearX} onClick={() => { setQuery(''); setPage(1); }} aria-label="Clear search"><X size={14} /></button>}
                        </label>
                        <div className="resp-range-group" style={styles.rangeGroup} role="group" aria-label="Registered date">
                            {DATE_RANGES.map(r => (
                                <button key={r.id} type="button"
                                    style={{ ...styles.rangeBtn, ...(range === r.id ? styles.rangeBtnActive : {}) }}
                                    onClick={() => { setRange(r.id); setPage(1); }}>
                                    {r.label}
                                </button>
                            ))}
                        </div>
                        {filtersActive && <button type="button" style={styles.clearAll} onClick={clearFilters}>Clear filters</button>}
                    </div>

                    {/* PENDING */}
                    {activeTab === 'pending' && (
                        <div>
                            {pending.length === 0 ? (
                                <div style={styles.empty}>
                                    <IconCheck size={34} color={C.green} />
                                    <p style={styles.emptyText}>{filtersActive ? 'No pending providers match your filters' : 'No pending approvals'}</p>
                                </div>
                            ) : (
                                paginate(pending, page, 10).map(v => <VendorCard key={v._id} v={v} isPending />)
                            )}
                            <Pagination page={page} total={pending.length} onChange={setPage} />
                        </div>
                    )}

                    {/* APPROVED */}
                    {activeTab === 'approved' && (
                        <div>
                            {approved.length === 0 ? (
                                <div style={styles.empty}>
                                    <IconStore size={34} color={C.dim} />
                                    <p style={styles.emptyText}>{filtersActive ? 'No approved providers match your filters' : 'No approved vendors yet'}</p>
                                </div>
                            ) : (
                                paginate(approved, page, 10).map(v => <VendorCard key={v._id} v={v} />)
                            )}
                            <Pagination page={page} total={approved.length} onChange={setPage} />
                        </div>
                    )}

                    {/* SERVICES TABLE */}
                    {activeTab === 'services' && (
                        <div className="resp-table-scroll" style={styles.tableBox}>
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        {['#', 'Service', 'Parent provider account', 'Category', 'Location', 'Price', 'Status', 'Actions'].map(h => (
                                            <th key={h} style={styles.th}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginate(shownServices, page, 10).map((s, i) => {
                                        const parentVendor = parentVendorFor(s);
                                        const parentName = parentVendor?.businessName || parentVendor?.ownerName || parentVendor?.name || 'Unlinked provider';
                                        return (
                                        <tr key={s._id}
                                            style={styles.tr}
                                            onMouseEnter={e => e.currentTarget.style.background = 'var(--admin-surface-hover)'}
                                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                            <td style={{ ...styles.td, color: C.dim }}>{String((page - 1) * 10 + i + 1).padStart(2, '0')}</td>
                                            <td style={styles.td}>
                                                <div style={{ fontWeight: '600', color: C.text }}>{s.businessName}</div>
                                                <div style={{ fontSize: '11px', color: C.dim, marginTop: '2px' }}>{s.ownerName}</div>
                                            </td>
                                            <td style={styles.td}>
                                                {parentVendor?._id ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => navigate(`/service-providers/${parentVendor._id}`)}
                                                        title="Open service provider account"
                                                        style={styles.parentAccountLink}
                                                    >
                                                        <span>{parentName}</span>
                                                        <small>{parentVendor.providerId || parentVendor.ownerName || 'Service provider account'}</small>
                                                    </button>
                                                ) : (
                                                    <span style={{ color: C.dim, fontSize: '12px' }}>Unlinked provider</span>
                                                )}
                                            </td>
                                            <td style={styles.td}>{s.category}</td>
                                            <td style={styles.td}>{s.city}{s.district ? `, ${s.district}` : ''}</td>
                                            <td style={{ ...styles.td, color: C.gold, fontWeight: '600' }}>
                                                {s.priceMin && s.priceMax
                                                    ? `₹${s.priceMin.toLocaleString()} – ₹${s.priceMax.toLocaleString()}`
                                                    : s.price || '—'}
                                            </td>
                                            <td style={styles.td}>
                                                {s.isVerified ? (
                                                    <span style={{ ...styles.chip, borderColor: C.green, color: C.green }}>
                                                        <IconCheck size={12} color={C.green} /> Verified
                                                    </span>
                                                ) : (
                                                    <span style={{ ...styles.chip, borderColor: C.amber, color: C.amber }}>
                                                        <IconClock size={12} color={C.amber} /> Pending
                                                    </span>
                                                )}
                                            </td>
                                            <td style={styles.td}>
                                                <div style={{ display: 'flex', gap: '8px' }}>
                                                    <button
                                                        style={{ ...styles.btn, borderColor: s.isVerified ? C.amber : C.green, color: s.isVerified ? C.amber : C.green }}
                                                        onClick={() => verifyService(s._id, s.isVerified)}>
                                                        {s.isVerified
                                                            ? <><IconClock size={13} color={C.amber} /> Unverify</>
                                                            : <><IconCheck size={13} color={C.green} /> Verify</>}
                                                    </button>
                                                    <button
                                                        style={{ ...styles.btn, borderColor: C.red, color: C.red }}
                                                        onClick={() => deleteService(s._id, s.businessName)}>
                                                        <IconTrash size={13} color={C.red} /> Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                        );
                                    })}
                                    {shownServices.length === 0 && (
                                        <tr>
                                            <td colSpan={8} style={{ ...styles.td, textAlign: 'center', color: C.dim, padding: '44px' }}>
                                                {filtersActive ? 'No services match your filters' : 'No services listed yet'}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {activeTab === 'services' && <Pagination page={page} total={shownServices.length} onChange={setPage} />}
                </div>
            </div>

            <ConfirmDialog
                open={Boolean(pendingToggle)}
                title={pendingToggle?.isActive === false ? 'Activate account?' : 'Deactivate account?'}
                message={pendingToggle?.isActive === false
                    ? `${pendingToggle?.businessName} will be able to log in again.`
                    : `${pendingToggle?.businessName} will not be able to log in to the website until you activate the account again. Their data is kept.`}
                confirmLabel={pendingToggle?.isActive === false ? 'Activate' : 'Deactivate'}
                danger={pendingToggle?.isActive !== false}
                onConfirm={confirmToggleAccount}
                onCancel={() => setPendingToggle(null)}
            />

            <ConfirmDialog
                open={Boolean(pendingDelete)}
                title={pendingDelete?.type === 'vendor' ? 'Delete Vendor?' : 'Delete Service?'}
                message={pendingDelete?.type === 'vendor'
                    ? 'This removes the vendor account entirely.'
                    : pendingDelete ? `Delete "${pendingDelete.name}"? This cannot be undone.` : ''}
                confirmLabel={pendingDelete?.type === 'vendor' ? 'Delete Vendor' : 'Delete Service'}
                danger
                onConfirm={confirmDelete}
                onCancel={() => setPendingDelete(null)}
            />
        </div>
    );
};

const styles = {
    inner: { padding: '28px' },
    statsRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '26px' },
    statCard: {
        position: 'relative',
        background: C.panel,
        border: `1px solid ${C.border}`,
        borderRadius: '14px',
        padding: '18px 20px 16px',
        overflow: 'hidden',
    },
    statAccent: { position: 'absolute', top: 0, left: 0, right: 0, height: '2px' },
    statTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
    statValue: { fontSize: '30px', fontWeight: '700', color: C.text, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' },
    statIcon: {
        width: '36px', height: '36px', borderRadius: '10px',
        border: '1px solid', background: 'var(--admin-surface-muted)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    statLabel: { fontSize: '11.5px', color: C.dim, marginTop: '8px', letterSpacing: '1px', textTransform: 'uppercase' },
    tabs: {
        display: 'inline-flex', gap: '4px', marginBottom: '22px',
        background: C.panel, border: `1px solid ${C.border}`,
        borderRadius: '12px', padding: '4px',
    },
    tabBtn: {
        padding: '9px 20px', border: 'none', borderRadius: '9px',
        fontSize: '13px', fontWeight: '600', cursor: 'pointer',
        background: 'transparent', color: C.dim, transition: 'all 0.15s ease',
        whiteSpace: 'nowrap',
    },
    tabBtnActive: {
        background: 'var(--admin-primary-soft)',
        color: C.gold,
        boxShadow: 'inset 0 0 0 1px color-mix(in srgb, var(--admin-primary) 35%, transparent)',
    },
    vendorCard: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '18px 20px', borderRadius: '14px', marginBottom: '12px', gap: '16px',
        background: C.panel, border: `1px solid ${C.border}`,
    },
    filterBar: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' },
    searchBox: { flex: '1 1 320px', display: 'flex', alignItems: 'center', gap: '9px', padding: '0 12px', minHeight: '42px', border: `1px solid ${C.border}`, borderRadius: '11px', background: C.panel },
    searchInput: { flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', color: C.text, font: 'inherit', fontSize: '13px' },
    clearX: { display: 'grid', placeItems: 'center', width: '22px', height: '22px', border: 'none', borderRadius: '50%', background: 'var(--admin-surface-muted)', color: C.dim, cursor: 'pointer' },
    rangeGroup: { display: 'inline-flex', gap: '4px', padding: '4px', border: `1px solid ${C.border}`, borderRadius: '11px', background: C.panel, flexWrap: 'wrap' },
    rangeBtn: { padding: '7px 13px', border: 'none', borderRadius: '8px', background: 'transparent', color: C.dim, fontSize: '12px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' },
    rangeBtnActive: { background: 'var(--admin-primary)', color: '#fff' },
    clearAll: { padding: '8px 14px', border: `1px solid ${C.border}`, borderRadius: '10px', background: C.panel, color: C.dim, fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
    vendorCardInactive: { opacity: 0.75, borderStyle: 'dashed' },
    vendorInfo: { display: 'flex', gap: '16px', alignItems: 'flex-start' },
    vendorAvatar: {
        width: '48px', height: '48px', borderRadius: '13px',
        background: 'var(--admin-primary-soft)', border: '1px solid color-mix(in srgb, var(--admin-primary) 40%, transparent)',
        color: C.gold, fontSize: '19px', fontWeight: '700',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    },
    vendorName: { fontWeight: '650', color: C.text, fontSize: '15px', marginBottom: '4px' },
    vendorMeta: { fontSize: '12px', color: C.dim, marginBottom: '2px' },
    vendorActions: { display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' },
    chip: {
        display: 'inline-flex', alignItems: 'center', gap: '6px',
        padding: '4px 11px', borderRadius: '999px',
        fontSize: '11.5px', fontWeight: '600',
        border: '1px solid', background: 'transparent',
        whiteSpace: 'nowrap',
    },
    btn: {
        display: 'inline-flex', alignItems: 'center', gap: '6px',
        padding: '7px 13px', borderRadius: '9px',
        fontSize: '12px', fontWeight: '600', cursor: 'pointer',
        border: '1px solid', background: 'transparent',
        transition: 'all 0.15s ease', whiteSpace: 'nowrap',
    },
    empty: {
        textAlign: 'center', padding: '56px',
        background: C.panel, border: `1px solid ${C.border}`, borderRadius: '14px',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px',
    },
    emptyText: { color: C.dim, fontSize: '14px', margin: 0 },
    tableBox: {
        background: C.panel, border: `1px solid ${C.border}`,
        borderRadius: '14px', overflow: 'hidden',
    },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: {
        padding: '13px 16px', background: 'var(--admin-surface-muted)',
        color: C.dim, fontSize: '11px', fontWeight: '650', textAlign: 'left',
        textTransform: 'uppercase', letterSpacing: '1.2px',
        borderBottom: `1px solid ${C.border}`,
    },
    tr: { transition: 'background 0.15s ease' },
    td: {
        padding: '13px 16px', fontSize: '13px', color: 'var(--admin-text)',
        borderBottom: '1px solid var(--admin-border)',
    },
    parentAccountLink: {
        display: 'flex', flexDirection: 'column', gap: '2px', padding: 0,
        border: 0, background: 'transparent', color: C.gold, font: 'inherit',
        fontSize: '12px', fontWeight: '700', textAlign: 'left', cursor: 'pointer',
    },
};

export default Services;
